import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { NavLink } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCirclePlus, faEllipsisH, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import Table from '../../components/Listing/Table';
import Button from '../../components/Form/Button';
import FabGroup from '../../components/FabGroup';
import HelpCategoryModal from '../../components/Modal/HelpCategoryModal';
import { usePageTitle } from '../../layouts/PageTitleContext';
import { API_BASE_URL } from '../../api';

export default function BackofficeHelpCategories() {
    const { setTitle, setTitleIcon, setTitleDescription } = usePageTitle();
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [keyword, setKeyword] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editCategory, setEditCategory] = useState(null);

    const load = useCallback(() => {
        setLoading(true);
        return axios.get(`${API_BASE_URL}/api/v1/backoffice/help/categories`, { withCredentials: true })
            .then(({ data }) => setCategories(data))
            .catch(() => toast.error('Impossible de charger les catégories.'))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        document.title = 'Backoffice — Centre d\'aide';
        load();
    }, [load]);

    useEffect(() => {
        setTitle("Centre d'aide");
        setTitleDescription('Catégories qui organisent les articles du centre d\'aide public');
        setTitleIcon('ti-book');
        return () => { setTitleDescription(null); setTitleIcon(null); };
    }, [setTitle, setTitleDescription, setTitleIcon]);

    const openCreate = () => { setEditCategory(null); setShowModal(true); };
    const openEdit = (category) => { setEditCategory(category); setShowModal(true); };

    const handleDelete = (category) => {
        axios.delete(`${API_BASE_URL}/api/v1/backoffice/help/categories/${category.id}`, { withCredentials: true })
            .then(() => {
                toast.success('Catégorie supprimée.');
                load();
            })
            .catch((err) => toast.error(err.response?.data?.message || 'Une erreur est survenue.'));
    };

    const columns = [
        {
            accessor: 'label',
            Header: 'Catégorie',
            sortable: true,
            minWidth: 220,
            Cell: row => (
                <NavLink to={`/backoffice/help/${row.id}`} style={{ fontWeight: 600, color: row.color || '#111827' }}>
                    {row.label}
                </NavLink>
            ),
        },
        {
            accessor: 'description',
            Header: 'Description',
            Cell: row => <span style={{ fontSize: 13, color: '#6b7280' }}>{row.description || '—'}</span>,
        },
        {
            accessor: 'articleCount',
            Header: 'Articles',
            className: 'text-center',
            sortable: true,
        },
        {
            accessor: 'actions',
            Header: '',
            className: 'col-actions',
            Cell: row => (
                <div className="cell-dropdown">
                    <button className="cell-actions-btn dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
                        <FontAwesomeIcon icon={faEllipsisH} />
                    </button>
                    <ul className="dropdown-menu dropdown-menu-end">
                        <li>
                            <NavLink to={`/backoffice/help/${row.id}`} className="dropdown-item d-flex align-items-center justify-content-between">
                                Voir les articles <FontAwesomeIcon icon={faChevronRight} />
                            </NavLink>
                        </li>
                        <li>
                            <button className="dropdown-item" onClick={() => openEdit(row)}>Modifier</button>
                        </li>
                        <li>
                            <Button
                                className="dropdown-item text-danger"
                                onConfirm={() => handleDelete(row)}
                            >
                                Supprimer
                            </Button>
                        </li>
                    </ul>
                </div>
            ),
        },
    ];

    return (
        <>
            <Table
                columns={columns}
                data={categories}
                mode="client"
                loading={loading}
                keyword={keyword}
                setKeyword={setKeyword}
                searchPlaceholder="Rechercher une catégorie..."
                rowId="id"
                pageSize={20}
                emptyMessage="Aucune catégorie"
                onRowClick={(row) => openEdit(row)}
            />

            <FabGroup buttons={[
                { id: 'new-category', icon: faCirclePlus, label: 'Nouvelle catégorie', onClick: openCreate },
            ]} />

            <HelpCategoryModal
                show={showModal}
                onClose={() => setShowModal(false)}
                onSaved={load}
                category={editCategory}
            />
        </>
    );
}
